import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";

import {
  TableContainer,
  TableHead,
  Table,
  TableBody,
  TableRow,
} from "@mui/material";

import { ClearLines } from "components/EditPage/ClearLines";
import { LineTableHeadingCell } from "components/formFields/LineTableCell";
import { LineItemFooter } from "components/formFields/Totals/Sales/LineItemFooter";
import { LineColumnFillHeading } from "components/formFields/line/LineColumnFillHeading";
import { LineColumnHeading } from "components/formFields/line/LineColumnHeading";
import { LineInventoryItemHeading } from "components/formFields/line/LineInventoryItem";
import { DragHandleHeading } from "components/utility/DragHandle";
import { EditLineButtonHeading } from "components/utility/EditLineButton";

import { i18n } from "services/i18nService";
import { expandItemGroup } from "services/sosInventoryService/expandItemGroup";

import { useClasses } from "hooks/useClasses.js";
import { useJobWorkcenters } from "hooks/useJobWorkcenters";
import { useLineItemHandlers } from "hooks/useLineItemHandlers";
import { usePrivileges } from "hooks/usePrivileges";

import { MemoizedLineItem } from "views/SalesTransactions/MemoizedLineItem";

import { USER_PRIVILEGE } from "appConstants";
import { EMPTY_LINE_ITEM } from "editConfig";

export function LineItems(props) {
  const {
    record,
    objectType,
    customer,
    lines,
    taxCodes,
    items,
    totals,
    itemsCount,
    addItem,
    lineHandler,
    setClearLines,
    lineItemsErrors,
    showPicked,
    showInvoiced,
    showShipped,
    showDueDate,
  } = props;

  const jobWorkcenters = useJobWorkcenters();
  const { classes, classesCount } = useClasses();

  // SETTINGS
  const retrieveQuantityOnHand = useSelector(
    (state) => state.userCompanySettings.settings.retrieveQuantityOnHand
  );
  const showVolumeOnSales = useSelector(
    (state) => state.userCompanySettings.settings.showVolumeOnSales
  );
  const showWeightOnSales = useSelector(
    (state) => state.userCompanySettings.settings.showWeightOnSales
  );
  const lineItemDiscountsEnabled = useSelector(
    (state) => state.userCompanySettings.settings.lineItemDiscountsEnabled
  );
  const classEnabled = useSelector(
    (state) => state.userCompanySettings.settings.classEnabled
  );
  const marginPricingEnabled = useSelector(
    (state) => state.userCompanySettings.settings.marginPricingEnabled
  );
  const jobEnabled = useSelector(
    (state) => state.userCompanySettings.settings.jobEnabled
  );
  const globalEnabled = useSelector(
    (state) => state.userCompanySettings.settings.globalEnabled
  );
  const uomEnabled = useSelector(
    (state) => state.userCompanySettings.settings.uomEnabled
  );

  const { hasPrivilegesOrIsAdmin } = usePrivileges();
  const viewCosts = hasPrivilegesOrIsAdmin(USER_PRIVILEGE.viewCosts);

  const {
    readOnlyLineIds,
    setLineEdit,
    handleDeleteLine,
    insertEmptyLine,
    handleDrop,
  } = useLineItemHandlers(
    record?.id,
    lines,
    lineHandler,
    EMPTY_LINE_ITEM[objectType]
  );

  if (!lines) {
    return null;
  }

  return (
    <>
      <TableContainer data-testing="lineItems" sx={{ overflowX: "initial" }}>
        <Table size="small" padding="none" stickyHeader>
          <TableHead>
            <TableRow sx={{ verticalAlign: "bottom", lineHeight: "1" }}>
              <DragHandleHeading />
              <EditLineButtonHeading recordId={record.id} />
              <ClearLines setClearLines={setClearLines} />
              <LineTableHeadingCell>
                {i18n("columns.Line")}
              </LineTableHeadingCell>
              <LineInventoryItemHeading lineHandler={lineHandler} />
              <LineColumnHeading labelCode="Description" />
              {retrieveQuantityOnHand && (
                <LineColumnHeading labelCode="Available" align="right" />
              )}
              <LineColumnHeading labelCode="Quantity" />
              {uomEnabled && <LineColumnHeading labelCode="UOM" />}
              {showWeightOnSales && <LineColumnHeading labelCode="Weight" />}
              {showVolumeOnSales && <LineColumnHeading labelCode="Volume" />}
              {lineItemDiscountsEnabled && (
                <LineColumnHeading labelCode="ListPrice" align="right" />
              )}
              {lineItemDiscountsEnabled && (
                <LineColumnHeading
                  labelCode="Discount"
                  sx={{ minWidth: "7rem" }}
                />
              )}
              {marginPricingEnabled && viewCosts && (
                <>
                  <LineColumnHeading labelCode="TotalCost" align="right" />
                  <LineColumnHeading
                    labelCode="Margin"
                    align="right"
                    sx={{ minWidth: "7rem" }}
                  />
                </>
              )}
              <LineColumnHeading labelCode="UnitPrice" align="right" />
              <LineColumnHeading labelCode="Amount" align="right" />
              {showPicked && (
                <LineColumnHeading
                  labelCode="Picked"
                  align="right"
                  sx={{ minWidth: "4rem" }}
                />
              )}
              {showShipped && (
                <LineColumnHeading
                  labelCode="Shipped"
                  align="right"
                  sx={{ minWidth: "4rem" }}
                />
              )}
              {showInvoiced && (
                <LineColumnHeading
                  labelCode="Invoiced"
                  align="right"
                  sx={{ minWidth: "4rem" }}
                />
              )}
              {showDueDate && (
                <LineColumnFillHeading
                  labelCode="DueDate"
                  field="duedate"
                  lineHandler={lineHandler}
                />
              )}
              {classEnabled && (
                <LineColumnFillHeading
                  labelCode="Class"
                  field="class"
                  lineHandler={lineHandler}
                />
              )}
              <LineColumnFillHeading
                lineHandler={lineHandler}
                labelCode="Tax"
                field={globalEnabled ? "taxCode" : "tax"}
              />
              {jobEnabled && (
                <LineColumnFillHeading
                  labelCode="Job"
                  field="jobWorkcenter"
                  lineHandler={lineHandler}
                />
              )}
            </TableRow>
          </TableHead>
          <DragDropContext onDragEnd={handleDrop}>
            <Droppable droppableId="lineItems">
              {(provided) => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {lines.map((line, index) => {
                    const lineReadOnly = readOnlyLineIds.has(index);

                    // Use same component with readOnly prop
                    return (
                      <MemoizedLineItem
                        key={line.id || `temp-${index}`}
                        line={line}
                        index={index}
                        record={record}
                        objectType={objectType}
                        customer={customer}
                        lineHandler={lineHandler}
                        expandItemGroup={(line) =>
                          expandItemGroup(line, lineHandler, objectType, record)
                        }
                        items={items}
                        itemsCount={itemsCount}
                        classes={classes}
                        classesCount={classesCount}
                        addItem={addItem}
                        taxCodes={taxCodes}
                        jobWorkcenters={jobWorkcenters}
                        lineItemsErrors={lineItemsErrors}
                        showPicked={showPicked}
                        showInvoiced={showInvoiced}
                        showShipped={showShipped}
                        showAvailable={retrieveQuantityOnHand}
                        showDueDate={showDueDate}
                        insertEmptyLine={insertEmptyLine}
                        isLastLine={index === lines.length - 1}
                        setLineEdit={setLineEdit}
                        lineReadOnly={lineReadOnly}
                        nextLineReadOnly={readOnlyLineIds.has(index + 1)}
                        handleDeleteLine={handleDeleteLine}
                      />
                    );
                  })}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </DragDropContext>
          <LineItemFooter
            totals={totals}
            lineHandler={lineHandler}
            objectType={objectType}
            recordId={record.id}
          />
        </Table>
      </TableContainer>
    </>
  );
}
